import { FC } from 'react';
import classNames from 'classnames';
import { ChevronRightIcon } from '@heroicons/react/outline';
import {
  getProviderActivityData,
  isProviderActivityMultiSignatureAssessment,
} from '../../../../lib/providerActivity';
import {
  ActivityListDataFragment,
  AssessmentSchemaType,
  SignatureUser,
} from '../../../../../generated/graphql';
import SignatureChip from '../../../SignatureChip';
import Signature from '../../../../svgs/Signature';
import { isProviderOnlyAssessment } from '../../../../lib/assessments';

const ActivitySummary: FC<{
  providerActivity: ActivityListDataFragment;
  followUpMode?: boolean;
}> = ({ providerActivity, followUpMode = false }) => {
  const { activityTitle, activitySubtitle, activityImage, ActivityThumbnail } =
    getProviderActivityData(providerActivity);

  // Check if this is a custom form with collaborative signatures
  const isMultiSignatureAssessment =
    isProviderActivityMultiSignatureAssessment(providerActivity);
  const isCustomForm =
    providerActivity.__typename === 'ActivityAssessment' &&
    providerActivity.assessment?.schemaType === AssessmentSchemaType.Custom;
  const isProviderOnly =
    providerActivity.__typename === 'ActivityAssessment' &&
    isProviderOnlyAssessment(providerActivity.assessment);

  // Note: library list queries do not fetch assessment.questions (some assessments have
  // unsupported question types like 'FileUpload' which can break the query).
  // So we only show signature chips for collaborative signatures (assessmentSignatureUsers).
  // For provider-only forms, we show a signature chip to indicate provider signatures are required.
  const showSignatureChips =
    isCustomForm && (isMultiSignatureAssessment || isProviderOnly);

  // Get signature users data for collaborative signatures
  const signatureUsers =
    isMultiSignatureAssessment &&
    providerActivity.__typename === 'ActivityAssessment'
      ? providerActivity.assessment?.assessmentSignatureUsers || []
      : [];

  // Practitioner (provider-only) forms require a provider signature. The library
  // list query intentionally does not fetch assessment.questions (see note above),
  // so we show a provider-signature chip for any provider-only form rather than
  // inspecting individual question types.
  const hasProviderSignatureChip =
    isProviderOnly && !isMultiSignatureAssessment;

  return (
    <div className="flex items-center">
      {Boolean(activityImage) ? (
        <img
          src={activityImage}
          className="mr-4 h-[36px] w-[36px] flex-none rounded-lg object-cover"
          alt="Activity thumbnail"
        />
      ) : (
        ActivityThumbnail && (
          <ActivityThumbnail className="mr-4 h-[36px] w-[36px] flex-none rounded-lg" />
        )
      )}
      <div className="flex-col text-green-150">
        <div
          className={classNames(
            'font-medium',
            !followUpMode ? 'line-clamp-2' : 'line-clamp-1',
          )}
        >
          {activityTitle}
        </div>
        <div className="mt-0 flex flex-wrap items-center gap-1">
          {isProviderOnly && (
            <>
              <span className="inline-flex items-center rounded-full bg-orange-25 px-1.5 py-0.5 text-small-label text-orange-125">
                Practitioner form
              </span>
              {(showSignatureChips && signatureUsers.length > 0) ||
              hasProviderSignatureChip ? (
                <span className="text-neutral-100"> | </span>
              ) : null}
            </>
          )}
          {activitySubtitle && !isProviderOnly && (
            <span className="text-small-caption text-neutral-125">
              {activitySubtitle}
            </span>
          )}
          {showSignatureChips && signatureUsers.length > 0 && (
            <>
              <Signature className="text-neutral-500 mr-1 h-2.5 w-2.5" />
              {signatureUsers
                .sort((a, b) => a.orderIndex - b.orderIndex)
                .map((signatureUser, index, array) => (
                  <div key={signatureUser.id} className="flex items-center">
                    <SignatureChip
                      signerType={signatureUser.signatureUserType}
                    />
                    {index < array.length - 1 && (
                      <ChevronRightIcon className="text-neutral-400 mx-1 h-3 w-3" />
                    )}
                  </div>
                ))}
            </>
          )}
          {hasProviderSignatureChip && (
            <>
              <Signature className="text-neutral-500 mr-1 h-2.5 w-2.5" />
              <SignatureChip signerType={SignatureUser.AssignedProviderUser} />
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ActivitySummary;
